import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Copy, Download, Printer, QrCode, Loader2 } from "lucide-react";

/**
 * Admin-only: renders a printable "Claim this business" QR that resolves to
 * /business/{id}?claim=1&ref={admin_referral_code}. Anyone who scans the QR,
 * lands on the page, signs up, and claims will credit the admin's referral
 * points (default 50 on signup, +100 on first paid month).
 *
 * Designed for in-person hand-outs: expo tables, walk-in visits, flyers,
 * business-card backs. Save to phone home screen for one-tap sharing.
 */
export default function ClaimQRDialog({ open, onOpenChange, business }) {
  const [refCode, setRefCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const qrRef = useRef(null);

  // Resolve production URL from the browser origin so the printed QR points
  // at whatever domain the admin is browsing on (preview vs prod).
  const appOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "https://theblkcommunity.com";

  const claimUrl = business
    ? `${appOrigin}/business/${business.business_id}?claim=1${refCode ? `&ref=${refCode}` : ""}`
    : "";

  useEffect(() => {
    if (!open) return;
    if (refCode) return; // cache across opens
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/referrals/me`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setRefCode(r.data?.code || "");
      })
      .catch(() => {
        // Non-fatal — QR still works, admin just won't get referral credit
        if (!cancelled) setRefCode("");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, refCode]);

  const copyLink = async () => {
    if (!claimUrl) return;
    try {
      await navigator.clipboard.writeText(claimUrl);
      toast.success("Claim link copied to clipboard.");
    } catch {
      toast.error("Couldn't access clipboard — long-press the URL below.");
    }
  };

  const downloadPNG = () => {
    if (!qrRef.current) return;
    const canvas = qrRef.current.querySelector("canvas");
    if (!canvas) return;
    const url = canvas.toDataURL("image/png");
    const safeName = (business?.name || "business")
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase();
    const a = document.createElement("a");
    a.href = url;
    a.download = `claim-qr-${safeName}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const printQR = () => {
    if (!qrRef.current) return;
    const canvas = qrRef.current.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const win = window.open("", "_blank", "width=600,height=800");
    if (!win) {
      toast.error("Pop-up blocked — allow pop-ups then try again.");
      return;
    }
    // Build the print page via DOM APIs instead of the deprecated
    // `document.write` — same output, no synchronous parser reflow.
    const doc = win.document;
    const escapeText = (s) =>
      String(s || "").replace(/[&<>"']/g, (c) => (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
      ));
    const bizName = business?.name || "Your business";

    doc.title = `Claim ${bizName}`;
    const style = doc.createElement("style");
    style.textContent =
      "body{font-family:Georgia,serif;text-align:center;padding:40px;color:#0A192F;}" +
      "h1{font-size:26px;margin:0 0 8px;}" +
      "h2{font-size:16px;margin:0 0 24px;color:#6b7280;font-weight:normal;}" +
      ".qr{margin:16px auto;padding:16px;border:1px solid #D4AF37;border-radius:12px;display:inline-block;background:#fff;}" +
      ".cta{font-size:14px;margin-top:20px;color:#0A192F;}" +
      ".url{font-family:monospace;font-size:11px;color:#6b7280;word-break:break-all;margin-top:8px;}" +
      ".brand{margin-top:32px;font-size:12px;color:#D4AF37;letter-spacing:2px;text-transform:uppercase;}";
    doc.head.appendChild(style);

    doc.body.innerHTML =
      `<h1>${escapeText(bizName)}</h1>` +
      `<h2>is reserved for you on The Blk Community</h2>` +
      `<div class="qr"><img src="${dataUrl}" width="320" height="320" alt="Claim QR" /></div>` +
      `<p class="cta"><strong>Scan with your phone camera</strong> to claim your profile in ~30 seconds.<br/>` +
      `Update photos, hours, products &amp; respond to reviews — free.</p>` +
      `<p class="url">${escapeText(claimUrl)}</p>` +
      `<p class="brand">The Blk Community &nbsp;·&nbsp; A Midas Touch Group LLC company</p>`;

    // Trigger the print dialog once the image loads.
    const img = doc.body.querySelector("img");
    if (img && !img.complete) {
      img.addEventListener("load", () => win.print(), { once: true });
    } else {
      setTimeout(() => win.print(), 100);
    }
  };

  if (!business) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="claim-qr-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-tbc-navy">
            <QrCode className="w-5 h-5 text-tbc-gold" />
            Claim QR — {business.name}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-3 py-2">
          {loading ? (
            <div className="h-[260px] flex items-center justify-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating…
            </div>
          ) : (
            <>
              <div
                ref={qrRef}
                className="p-4 bg-white rounded-xl border-2 border-tbc-gold/40"
                data-testid="claim-qr-canvas"
              >
                <QRCodeCanvas
                  value={claimUrl}
                  size={240}
                  level="M"
                  includeMargin={false}
                  fgColor="#0A192F"
                  bgColor="#ffffff"
                />
              </div>
              <p className="text-xs text-gray-500 text-center leading-relaxed max-w-xs">
                Anyone who scans, signs up, and claims{" "}
                <strong>{business.name}</strong> credits <strong>your</strong> referral
                points. Print it, save it to your phone, or hand it out at events.
              </p>
              <div className="w-full bg-gray-50 rounded-md px-3 py-2 text-[11px] font-mono text-gray-600 break-all border border-gray-200" data-testid="claim-qr-url">
                {claimUrl}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={copyLink}
            disabled={loading}
            data-testid="claim-qr-copy-btn"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" />
            Copy link
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={downloadPNG}
            disabled={loading}
            data-testid="claim-qr-download-btn"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            Download PNG
          </Button>
          <Button
            type="button"
            onClick={printQR}
            disabled={loading}
            className="bg-tbc-navy text-white hover:bg-tbc-navy/90"
            data-testid="claim-qr-print-btn"
          >
            <Printer className="w-3.5 h-3.5 mr-1.5" />
            Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
